import { round } from "lodash-es";
import { defineStore } from "pinia";

import { loadReactiveUser } from "@/backend/http/user";
import { captureMessage } from "@/error/sentry";
import type { Sticker } from "@/model/StickerType";
import type { AlmSourceId, Color, Id, IdMap, StatusClass } from "@/model/baseTypes";
import type { Board, BoardId } from "@/model/board";
import type { BoardCard, Card, CardMeta, LinkedSticky, Reaction, Reactions } from "@/model/card";
import { reactions } from "@/model/card";
import type { RelativeCoordinate } from "@/model/coordinates";
import { relativeCoord } from "@/model/coordinates";
import type { Team } from "@/model/session";
import type { StatusClassDistribution } from "@/model/status";
import type { StickyType } from "@/model/stickyType";
import { isDependency } from "@/model/stickyType";
import type { PersonalUser } from "@/model/user";
import { firstValue } from "@/utils/object";
import { removeNonPrintable } from "@/utils/string";

import { useAlmItemTypeStore } from "./almItemType";
import { useBoardStore } from "./board";
import { useBoardsStore } from "./boards";
import { useLinkStore } from "./link";
import { isStickyTypeAlmSynced, useStickyTypeStore } from "./stickyType";
import { useTeamStore } from "./team";
import { useUserStore } from "./user";

export type CardEvent = Id & { pos: RelativeCoordinate } & BoardId &
  Partial<Card> &
  Pick<
    Card,
    "type" | "priority" | "teamId" | "iterationId" | "flagType" | "objectives" | "reactions"
  > &
  Pick<CardMeta, "zIndex" | "shouldAnimate">;

export const useCardStore = defineStore("card", {
  state: () => ({ cards: {} as IdMap<Card> }),
  getters: {
    /**
     * Returns the board card corresponding to the given card's *origin* card.
     * Falls back to a synthetic BoardCard when no origin board exists
     * (e.g. workitems accidentally belonging to an external team)
     * so callers like the breakdown view can still render them.
     */
    originBoardCard: () => {
      return (card: Card): BoardCard => {
        const board = useBoardsStore().boardByOriginCard(card);
        return board?.cards[card.id] ?? orphanBoardCard(card);
      };
    },
  },
  actions: {
    add(cardProperties: CardEvent) {
      const board = useBoardsStore().boards[cardProperties.boardId];
      const id = cardProperties.id;
      const card: Card = {
        id,
        groupId: cardProperties.groupId,
        status: cardProperties.status,
        text: cardProperties.text || "",
        points: cardProperties.points || 0,
        priority: cardProperties.priority,
        iterationId: cardProperties.iterationId === undefined ? null : cardProperties.iterationId,
        editor: null,
        type: cardProperties.type,
        flagType: cardProperties.flagType,
        almId: cardProperties.almId || "",
        almSourceId: cardProperties.almSourceId ?? null,
        almIssueUrl: cardProperties.almIssueUrl,
        teamId: cardProperties.teamId || null,
        precondTeam: cardProperties.precondTeam,
        dependTeam: cardProperties.dependTeam,
        risk: cardProperties.risk,
        objectives: cardProperties.objectives || [],
        reactions: cardProperties.reactions,
        artId: cardProperties.artId || null,
        assignee: cardProperties.assignee || null,
        reporter: cardProperties.reporter || null,
        linkedStickies: cardProperties.linkedStickies || [],
        temporaryLinks: [],
        statusDistribution: cardProperties.statusDistribution,
      };
      const cardMeta: CardMeta = {
        pos: cardProperties.pos,
        zIndex: cardProperties.zIndex || board.maxZ,
        mark: "normal",
        editing: false,
        dragging: false,
        isHighlighted: false,
        isRelatedToHighlighted: false,
        dependencyTeamFilter: null,
        shouldAnimate: cardProperties.shouldAnimate,
      };
      this.cards[id] = card;
      board.cards[id] = { data: card, meta: cardMeta };
      if (cardProperties.zIndex > board.maxZ) {
        board.maxZ = cardProperties.zIndex;
      }
      if (cardProperties.groupId) {
        useLinkStore().addToLinkGroup(
          cardProperties.groupId,
          cardProperties.boardId,
          board.cards[id],
        );
      }
      useLinkStore().setCardLinkStates(card);
      this.setType({ id, type: cardProperties.type.id, boardId: cardProperties.boardId });
      if (cardProperties.almSourceId !== undefined) {
        this.setAlmSource({ id, almSourceId: cardProperties.almSourceId });
      }
      return id;
    },

    setAlmSource(e: Id & { almSourceId: AlmSourceId | null }) {
      const card = this.cards[e.id];
      card.almSourceId = e.almSourceId ?? null;
    },

    setPriority(e: Id & { priority: number; priorities?: boolean }) {
      const prios = this.cards[e.id].type.priorities;
      const use = e.priorities === undefined ? !!prios : e.priorities;
      const priority = use ? e.priority || firstValue(prios!).value : round(e.priority || 0, 2);
      this.cards[e.id].priority = priority;
      return { use, priority };
    },

    setPoints(e: Id & { points: number }) {
      this.cards[e.id].points = e.points;
    },

    setFlag(e: Id & { flagType: Sticker | null }) {
      this.cards[e.id].flagType = e.flagType;
    },

    setRisk(e: Id & Pick<Card, "risk">) {
      this.cards[e.id].risk = e.risk;
    },

    setText(e: Id & { text: string }) {
      this.cards[e.id].text = e.text;
    },

    setIteration(e: Id & { iterationId: number | null }) {
      const card = this.cards[e.id];
      if (card.iterationId !== e.iterationId) {
        card.iterationId = e.iterationId;
        useLinkStore().setCardLinkStates(card);
        return true;
      }
    },

    setTeam(e: Id & { teamId: string | null }) {
      const card = this.cards[e.id];
      card.teamId = e.teamId;
      useLinkStore().setCardLinkStates(card);
    },

    setArt(e: Id & { artId: string | null }) {
      const card = this.cards[e.id];
      card.artId = e.artId;
      useLinkStore().setCardLinkStates(card);
    },

    setPrecondTeam(e: Id & { teamId?: string; teamName?: string }) {
      this.cards[e.id].precondTeam = useTeamStore().findTeam({ id: e.teamId, name: e.teamName });
    },

    setDependTeam(e: Id & { teamId?: string; teamName?: string }) {
      this.cards[e.id].dependTeam = useTeamStore().findTeam({
        id: e.teamId,
        name: e.teamName,
        withExternal: true,
      });
    },

    setDependAction(e: Id & { dependTeam: Team }) {
      const board = useBoardsStore().boardByTypeOptional("team", { teamId: e.dependTeam.id });
      const card = this.cards[e.id];
      card.flagType = { icon: "flag", color: "red" };
      card.precondTeam = useTeamStore().current;
      card.dependTeam = e.dependTeam;
      return { board, card };
    },

    setType(e: Id & { boardId?: string; type: string }) {
      const card = this.cards[e.id];
      const board = e.boardId ? useBoardsStore().boards[e.boardId] : useBoardStore().currentBoard();
      const type = useStickyTypeStore().findStickyType(e.type);
      const changes: { board: Board; props: Partial<Card>; priorities: boolean } = {
        board,
        props: { type, teamId: card.teamId, iterationId: card.iterationId },
        priorities: false,
      };
      if (card.type && card.type.priorities !== type.priorities) {
        const { priority, use } = this.setPriority({
          id: e.id,
          priority: type.priorities ? firstValue(type.priorities).value : 0,
          priorities: !!type.priorities,
        });
        changes.props.priority = priority;
        changes.priorities = use;
      }
      card.type = type;
      if (board.type === "team" && card.type.origin === "backlog") {
        const backlogAlmSources = useBoardsStore().boardByType("backlog").almSources;
        if (backlogAlmSources.length === 1) {
          changes.props.almSourceId = backlogAlmSources[0].id;
        } else if (backlogAlmSources.length === 0) {
          changes.props.almSourceId = null;
        }
      }
      if (type.functionality === "dependency" && card.teamId && !card.precondTeam) {
        card.precondTeam = useTeamStore().findTeam({ id: card.teamId });
        changes.props.precondTeam = card.precondTeam;
      }
      if (type.functionality === "workitem" && !card.status) {
        card.status = useAlmItemTypeStore().calcStatus(
          undefined,
          type,
          card.teamId,
          board.artId,
          card.almSourceId,
        );
        changes.props.status = card.status;
      }
      if (type.functionality === "note" && card.reactions === undefined) {
        const emptyReactions = reactions.map((reaction) => [reaction, []]);
        useCardStore().setReactions(e.id, Object.fromEntries(emptyReactions));
      }
      if (isStickyTypeAlmSynced(type)) {
        card.text = removeNonPrintable(card.text);
        changes.props.text = card.text;
      }
      return changes;
    },

    setObjectives(cardId: Card["id"], objectives: Card["objectives"] | undefined) {
      if (!objectives) {
        return;
      }
      const card = this.cards[cardId];
      if (!card) {
        captureMessage("Tried to set objectives but it is not in the state", {
          info: { cardId },
        });
        return;
      }
      card.objectives = [...objectives];
    },

    setGroup(e: Id & { groupId: string }) {
      const card = this.cards[e.id];
      if (card) {
        card.groupId = e.groupId;
        for (const boardId in useBoardsStore().boards) {
          const board = useBoardsStore().boards[boardId];
          const boardCard = board.cards[e.id];
          if (boardCard) {
            useLinkStore().addToLinkGroup(e.groupId, boardId, boardCard);
          }
        }
      }
    },

    setAlmId(e: Id & { almId: string }) {
      const card = this.cards[e.id];
      card.almId = e.almId;
    },

    setAlmIssueUrl(e: Id & { almIssueUrl: string }) {
      const card = this.cards[e.id];
      card.almIssueUrl = e.almIssueUrl;
    },

    readOnly(e: Id & ({ readOnly: true; userId: string } | { readOnly: false })) {
      const card = this.cards[e.id];
      if (card) {
        // card might just have been deleted
        if (e.readOnly) {
          card.editor = loadReactiveUser({ id: e.userId, caller: "editor" });
        } else {
          card.editor = null;
        }
      }
    },

    setStatus(
      board: Board,
      cardId: string,
      statusName: string,
      options: { transition?: string; statusClass?: StatusClass } = {},
    ) {
      const card = this.cards[cardId];
      const status = useAlmItemTypeStore().findStatusForCard(
        statusName,
        card,
        board,
        options.statusClass,
      );
      card.status = status;
      card.transition = options.transition;
      return status;
    },

    updateCardsStatus(card: Card, board: Board) {
      if (!card.status) {
        return;
      }
      const status = useAlmItemTypeStore().findStatusForCard(card.status.name, card, board);
      for (const id in this.cards) {
        const c = this.cards[id];
        if (hasSameStatus(c, card)) {
          c.status = status;
        }
      }

      function hasSameStatus(a: Card, b: Card) {
        return (
          a.status?.id === b.status?.id &&
          a.type.id === b.type.id &&
          a.teamId === b.teamId &&
          a.almSourceId === b.almSourceId
        );
      }
    },

    hasCurrentUserReaction(cardId: string, reaction: Reaction): boolean {
      return (
        this.cards[cardId].reactions?.[reaction]?.some(
          (user) => user.id === useUserStore().technicalUser.id,
        ) ?? false
      );
    },

    setReactions(cardId: string, reactions: Reactions) {
      this.cards[cardId].reactions = reactions;
    },

    setAssignee(cardId: string, user: PersonalUser | null) {
      this.cards[cardId].assignee = user;
    },

    setReporter(cardId: string, user: PersonalUser | null) {
      this.cards[cardId].reporter = user;
    },

    setLinkedStickies(cardId: string, linkedStickies: LinkedSticky[]) {
      this.cards[cardId].linkedStickies = linkedStickies;
      useLinkStore().setCardLinkStates(this.cards[cardId]);
    },

    setStatusDistribution(cardId: string, statusDistribution?: StatusClassDistribution) {
      this.cards[cardId].statusDistribution = statusDistribution;
    },

    delete({ id: cardId, boardId }: Id & BoardId) {
      const card = this.cards[cardId];
      const board = useBoardsStore().boards[boardId];
      const existsOnTheBoard = board?.cards[cardId];

      if (existsOnTheBoard && useBoardStore().activeCardId === cardId) {
        useBoardStore().setActiveCardId(null);
      }
      if (board) {
        useBoardStore().unselectSticky(cardId, board);
      }
      if (!card || !existsOnTheBoard) {
        // might already be deleted
        return false;
      }
      useLinkStore().removeFromLinkGroup(card, boardId);
      delete this.cards[cardId];
      for (const b in useBoardsStore().boards) {
        delete useBoardsStore().boards[b].cards[cardId];
      }
      return true;
    },
    updateStickyType(type: StickyType) {
      for (const id in this.cards) {
        const card = this.cards[id];
        if (card.type.id === type.id) {
          card.type = type;
        }
      }
    },
  },
});

export function mirrorOriginId(card: Card) {
  if (card.groupId && card.groupId !== card.id) {
    return card.groupId;
  }
}

export function isCardFromTeam(card: Card, teamId?: string) {
  return card.type.origin === "team" && !!teamId && teamId === card.teamId;
}

type CardColorInput = Pick<Card, "type"> & Partial<Pick<Card, "teamId" | "artId" | "precondTeam">>;

/**
 * Get the color of a card on a given board.
 * @returns The color of the card.
 */
export function cardColor(card: CardColorInput, board: Board): Color {
  if (board.type === "backlog" && card.type.origin === "backlog") {
    return card.teamId ? card.type.altColor : card.type.color;
  }

  if (board.type === "solution_backlog" && card.type.origin === "solution_backlog") {
    return card.artId ? card.type.altColor : card.type.color;
  }

  if (isDependency(card)) {
    return board.type === "team" && card.precondTeam?.id === board.team.id
      ? card.type.color
      : card.type.altColor;
  }

  return card.type.color;
}

/**
 * Renders a synthetic board card when none exists
 * (e.g. 'origin card' for workitems belonging to an external team)
 */
function orphanBoardCard(card: Card): BoardCard {
  return {
    data: card,
    meta: {
      pos: relativeCoord(0, 0),
      zIndex: 0,
      mark: "normal",
      editing: false,
      dragging: false,
      isHighlighted: false,
      isRelatedToHighlighted: false,
      dependencyTeamFilter: null,
    },
  };
}
